import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  ExtendTimeButton,
  TimeExtensionHistoryButton,
} from '@exam-center/exam-event-time-extension';
import { TimePostponementButton } from '@exam-center/exam-event-time-postponement';
import {
  ExamEventStatus,
  ExamType,
  useGetExamEventParticipatedCoursesQuery,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  ExamScheduleSummary,
  ExamSessionList,
  ExamineeMonitorListSection,
  ProctorList,
  ProctoringDetailsPermissionRoute,
  useProctoringDetailsModal,
} from '@exam-center/proctoring-details';
import { useParams } from '@exam-center/router';
import { useTimeGate } from '@exam-center/server-time';
import { AddIcon, Alert, Button, Flex, PaperSection, Text } from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { adjustTime, dayjs } from '@shared/utils';
import { useMemo } from 'react';

export default function ProctoringDetailsPage() {
  const { examCenterCode, examEventId } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/details',
  );
  const { setAction } = useProctoringDetailsModal();
  const { examEvent } = useExamEventDetails();
  const { data: participatedCoursesData, loading: participatedCoursesLoading } =
    useGetExamEventParticipatedCoursesQuery({
      variables: { input: { examEventId } },
      skip: !examEventId || examEvent.sessions.length !== 1,
    });
  const allowedStatuses: ExamEventStatus[] = [
    ExamEventStatus.InProgress,
    ExamEventStatus.Scheduled,
    ExamEventStatus.ReadyToExam,
  ];

  const allowedToAddProctor = allowedStatuses.includes(examEvent.status);

  const currentSession = examEvent.sessions[0];
  const participatedCourses =
    participatedCoursesData?.ecGetExamEventParticipatedCourses.examEventCourses ?? [];

  const adjustedExamStartTime = useMemo(
    () => dayjs(examEvent.examStartedAt).add(examEvent.postponeStartDurationInMs ?? 0, 'ms'),
    [examEvent.examStartedAt, examEvent.postponeStartDurationInMs],
  );
  const examDayStart = useMemo(
    () => adjustedExamStartTime.startOf('day').toDate(),
    [adjustedExamStartTime],
  );
  const nextExamDayStart = useMemo(
    () => adjustedExamStartTime.add(1, 'day').startOf('day').toDate(),
    [adjustedExamStartTime],
  );

  const onClickTimeExtension = () => {
    setAction({ action: 'extend-time', data: { sessionId: currentSession.id } });
  };

  const onClickTimePostponement = () => {
    setAction({ action: 'postpone-time' });
  };

  const isOnOrAfterExamDayStart = useTimeGate(examDayStart);
  const isOnOrAfterNextExamDayStart = useTimeGate(nextExamDayStart);
  const isExamToday = isOnOrAfterExamDayStart && !isOnOrAfterNextExamDayStart;

  const sortedSessions = useMemo(
    () => [...examEvent.sessions].sort((a, b) => a.round - b.round),
    [examEvent.sessions],
  );
  const lastSession = sortedSessions[sortedSessions.length - 1];
  const adjustedLastSessionStartTime = useMemo(
    () => adjustTime(lastSession.startedAt, lastSession.postponeDurationInMs ?? 0),
    [lastSession.postponeDurationInMs, lastSession.startedAt],
  );

  const allowedExamTypesForTimePostponement = [ExamType.Computer];

  const allowedStatusesForTimePostponement: ExamEventStatus[] = [
    ExamEventStatus.Scheduled,
    ExamEventStatus.ReadyToExam,
    ExamEventStatus.InProgress,
  ];

  const isTimePostponementVisible =
    isExamToday &&
    allowedExamTypesForTimePostponement.includes(examEvent.examType) &&
    allowedStatusesForTimePostponement.includes(examEvent.status);

  const isTimePostponementDisabled = useTimeGate(adjustedLastSessionStartTime);

  function onClickTimeExtensionHistory() {
    setAction({ action: 'time-extension-history', data: { sessionId: currentSession.id } });
  }

  return (
    <WithPermission
      requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.VIEW}
      errorElement={ProctoringDetailsPermissionRoute}
      slotProps={{ errorElement: { examCenterCode, examEventId } }}
    >
      <Flex sx={{ flexDirection: 'column', height: '100%', overflow: 'auto' }}>
        <Flex
          sx={(theme) => ({
            flex: 1,
            p: theme.spacing(6),
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          <ExamScheduleSummary
            endAdornment={
              <Flex sx={{ gap: 4 }}>
                <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE}>
                  <ExtendTimeButton
                    loading={participatedCoursesLoading}
                    hidden={examEvent.sessions.length !== 1}
                    currentSession={currentSession}
                    participatedCourses={participatedCourses}
                    postponeDuration={currentSession.postponeDurationInMs ?? 0}
                    onClick={onClickTimeExtension}
                  />
                  <TimePostponementButton
                    hidden={!isTimePostponementVisible}
                    disabled={isTimePostponementDisabled}
                    onClick={onClickTimePostponement}
                  />
                </WithPermission>
                <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.VIEW}>
                  <TimeExtensionHistoryButton
                    loading={participatedCoursesLoading}
                    hidden={examEvent.sessions.length !== 1}
                    currentSession={currentSession}
                    participatedCourses={participatedCourses}
                    postponeDuration={currentSession.postponeDurationInMs ?? 0}
                    onClick={onClickTimeExtensionHistory}
                  />
                </WithPermission>
              </Flex>
            }
          />
          <PaperSection
            title='ผู้คุมสอบ'
            sx={{
              width: '100%',
              flexDirection: 'column',
              height: 'fit-content',
            }}
            endAdornment={
              <WithPermission requiredPermission={APP_PERMISSIONS.PROCTORING.DETAILS.UPDATE}>
                {allowedToAddProctor ? (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setAction({ action: 'add-proctor' })}
                  >
                    เพิ่มผู้คุมสอบ
                  </Button>
                ) : null}
              </WithPermission>
            }
          >
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(6),
                p: theme.spacing(6),
              })}
            >
              <Alert severity='info' sx={{ width: '100%', fontWeight: 400 }}>
                <Text>
                  ศูนย์ทดสอบต้องมีเจ้าหน้าที่คุมสอบอย่างน้อย 2 คนต่อ 1 รอบสอบ
                  และจัดเจ้าหน้าที่ประจำห้องสอบเพียงพอในอัตราส่วนเจ้าหน้าที่ 1 คนต่อผู้เข้าสอบ 40 คน{' '}
                  <Text
                    component='span'
                    sx={{
                      textDecoration: 'underline',
                    }}
                  >
                    เป็นไปตามมาตรฐานห้องสอบ
                  </Text>
                </Text>
              </Alert>
              <ProctorList />
            </Flex>
          </PaperSection>
          <ExamSessionList />
          <ExamineeMonitorListSection />
        </Flex>
      </Flex>
    </WithPermission>
  );
}
